<template>
  <v-card class="card-shadow px-4 py-2 overflow-hidden border-radius-xl">
    <v-card-title class="d-flex align-center justify-space-between flex-wrap">
      <div class="d-flex align-center flex-wrap">
        <h4 class="mb-4 text-h4 text-typo font-weight-bold mr-4">排班資訊</h4>
        <v-select
          v-model="viewYear"
          :items="yearOptions"
          label="年份"
          dense
          outlined
          class="mr-2 mb-4"
          style="max-width: 100px"
          hide-details
          @change="viewScheduleByYM"
        ></v-select>
        <v-select
          v-model="viewMonth"
          :items="monthOptions"
          label="月份"
          dense
          outlined
          class="mr-2 mb-4"
          style="max-width: 100px"
          hide-details
          @change="viewScheduleByYM"
        ></v-select>
        <v-btn
          color="info"
          elevation="0"
          :ripple="false"
          height="40"
          class="
            font-weight-bold
            text-capitalize
            btn-info
            bg-gradient-info
            mr-2
            mb-4
          "
          @click="viewScheduleByYM"
        >
          查詢排班
        </v-btn>
        <!-- 匯出空白班表 -->
        <v-btn
          color="info"
          elevation="0"
          :ripple="false"
          height="40"
          class="
            font-weight-bold
            text-capitalize
            btn-default
            bg-gradient-default
            mr-2
            mb-4
          "
          @click="exportEmptySchedule"
        >
          匯出空白班表
        </v-btn>
        <v-btn
          color="info"
          elevation="0"
          :ripple="false"
          height="40"
          class="
            font-weight-bold
            text-capitalize
            btn-default
            bg-gradient-default
            mr-2
            mb-4
          "
          @click="exportHourlySchedule"
        >
          匯出時薪班表
        </v-btn>
      </div>
      <div class="d-flex">
        <v-btn
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-capitalize
            btn-success
            bg-gradient-success
            px-4
            mb-4
          "
          @click="openShiftDialog"
        >
          <v-icon left>mdi-calendar-clock</v-icon>
          班表設定
        </v-btn>
        <v-btn
          elevation="0"
          :ripple="false"
          height="43"
          class="
            font-weight-bold
            text-capitalize
            btn-primary
            bg-gradient-primary
            px-4
            ml-2
            mb-4
          "
          @click="importSchedule"
        >
          <v-icon left>mdi-import</v-icon>
          匯入排班
        </v-btn>
      </div>
    </v-card-title>
    <v-card-text>
      <h6 class="text-subtitle-1 font-weight-bold mb-3">班別設定</h6>
      <v-simple-table dense class="mb-6">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">班別代碼</th>
              <th class="text-left">名稱</th>
              <th class="text-left">上班 / 下班</th>
              <th class="text-left">休息分鐘</th>
              <th class="text-left">午休</th>
              <!-- <th class="text-left">彈性</th> -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="shift in shiftTypes" :key="shift.id">
              <td>{{ shift.code || "-" }}</td>
              <td>{{ shift.name || "未命名" }}</td>
              <td>
                {{
                  shift.clock_in_time
                    ? `${shift.clock_in_time} - ${shift.clock_out_time}`
                    : "無時間設定"
                }}
              </td>
              <td>{{ shift.break_minutes || "-" }}</td>
              <td>
                {{
                  shift.lunch_break_start && shift.lunch_break_end
                    ? `${shift.lunch_break_start} - ${shift.lunch_break_end}`
                    : "—"
                }}
              </td>
              <!-- <td>{{ shift.flexible ? "是" : "否" }}</td> -->
            </tr>
            <tr v-if="shiftTypes.length === 0">
              <td colspan="6" class="text-center text--disabled">
                尚未設定班別
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <v-dialog v-model="importDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>匯入排班</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="importFile"
            label="選擇 Excel 檔案"
            accept=".xlsx,.xls"
            show-size
            :rules="[(v) => !!v || '請選擇檔案']"
          ></v-file-input>
          <v-select
            v-model="importYear"
            :items="yearOptions"
            label="年份"
            dense
            outlined
          ></v-select>
          <v-select
            v-model="importMonth"
            :items="monthOptions"
            label="月份"
            dense
            outlined
          ></v-select>
          <v-select
            v-model="scheduleType"
            :items="scheduleTypeOptions"
            label="排班類型"
            dense
            outlined
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelImport">取消</v-btn>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-warning
              bg-gradient-warning
              py-3
              px-6
            "
            @click="validateImport"
            >驗證</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <schedule-entry-widget
      v-if="showScheduleEntry"
      ref="scheduleEntryWidget"
      :entries="importedEntries"
      :errors="importedErrors"
      :warnings="importedWarnings"
      :national-holiday-summaries="importedNationalHolidaySummaries"
      :year="importYear"
      :month="importMonth"
      :schedule_type="scheduleType"
      :shift-types="shiftTypes"
      :show-back-button="true"
      :show-shift-button="true"
      :can-import="canImport"
      @update:schedule_type="(val) => (scheduleType = val)"
      @open-shift-dialog="openShiftDialog"
      @show-table="showScheduleEntry = false"
      @import="performImport"
      @view-history="openHistoryDialog"
      @clear="
        importedEntries = [];
        importedErrors = [];
        importedWarnings = [];
        showScheduleEntry = false;
        canImport = false;
      "
    />

    <v-dialog v-model="historyDialog.visible" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-history</v-icon>
          歷史紀錄 - {{ historyDialog.empName }}
          <v-spacer />
          <v-btn icon @click="historyDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="max-height: 500px">
          <v-simple-table v-if="historyDialog.records.length > 0" dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>修改者</th>
                  <th>狀態概要</th>
                  <th>結餘使用</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in historyDialog.records" :key="h.id">
                  <td>{{ formatDateTime(h.create_time) }}</td>
                  <td>
                    {{ h.modifier && h.modifier.name ? h.modifier.name : "-" }}
                  </td>
                  <td
                    class="text-caption"
                    style="
                      max-width: 300px;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                  >
                    {{ summarizeDayStatuses(h) }}
                  </td>
                  <td class="text-caption">
                    <span v-if="h.extra && h.extra.nationalHolidayCarryOver">
                      使用結餘
                      {{ h.extra.nationalHolidayCarryOver.usedCarryOver }} 天
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <v-btn
                      x-small
                      class="
                        font-weight-bold
                        text-capitalize
                        btn-primary
                        bg-gradient-primary
                        py-4
                      "
                      :loading="historyDialog.rollingBack === h.id"
                      :disabled="historyDialog.rollingBack !== null"
                      @click="confirmRollback(h)"
                    >
                      <v-icon x-small left>mdi-undo</v-icon>
                      還原
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-else class="text-center text--disabled py-4">無歷史紀錄</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <shift-type-dialog
      :visible.sync="shiftDialog.visible"
      :company-id="companyId"
      @created="fetchShiftTypes"
      @deleted="fetchShiftTypes"
    />
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackbar.show = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import ScheduleEntryWidget from "./ScheduleEntryWidget.vue";
import ShiftTypeDialog from "./ShiftTypeDialog.vue";
import { formatScheduleType } from "@/utils/formatters";

export default {
  name: "CompanySchedulingInfo",
  components: {
    ScheduleEntryWidget,
    ShiftTypeDialog,
  },
  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      yearOptions: [],
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      schedules: [],
      scheduleTypeOptions: [],
      shiftTypes: [],
      loading: false,
      importDialog: false,
      importFile: null,
      importYear: new Date().getFullYear(),
      importMonth: new Date().getMonth() + 1,
      viewYear: new Date().getFullYear(),
      viewMonth: new Date().getMonth() + 1,
      showScheduleEntry: false,
      importedEntries: [],
      importedErrors: [],
      importedWarnings: [],
      importedNationalHolidaySummaries: [],
      shiftDialog: {
        visible: false,
      },
      scheduleType: null,
      snackbar: {
        show: false,
        text: "",
        color: "",
        timeout: 3000,
      },
      canImport: false,
      historyDialog: {
        visible: false,
        entryId: null,
        empName: "",
        records: [],
        rollingBack: null,
      },
    };
  },
  created() {
    this.generateYearOptions();
    this.fetchCompanySchedules();
    this.fetchShiftTypes();
  },
  watch: {
    companyId(newVal, oldVal) {
      if (!newVal || newVal === oldVal) return;
      this.showScheduleEntry = false;
      this.importedEntries = [];
      this.importedErrors = [];
      this.importedWarnings = [];
      this.importedNationalHolidaySummaries = [];
      this.scheduleType = null;
      this.fetchCompanySchedules();
      this.fetchShiftTypes();
    },
    importFile() {
      this.canImport = false;
    },
    importYear() {
      this.canImport = false;
    },
    importMonth() {
      this.canImport = false;
    },
    scheduleType() {
      this.canImport = false;
    },
  },
  methods: {
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2,
      ];
    },
    async fetchShiftTypes() {
      try {
        const response = await this.$axios.get("/api/scheduling/shift-types", {
          params: { company_id: this.companyId },
        });
        this.shiftTypes = response.data.data || [];
      } catch (error) {
        console.error("Error fetching shift types:", error);
      }
    },
    transformScheduleEntries(entries) {
      if (!entries || !Array.isArray(entries)) return [];
      return entries.map((entry) => {
        const days = {};
        for (let d = 1; d <= 31; d += 1) {
          if (entry.days && entry.days[d]) {
            days[d] = {
              status: entry.days[d].status ?? null,
              shift_type_id: entry.days[d].shift_type_id ?? null,
              clock: entry.days[d].clock ?? null,
            };
          } else {
            days[d] = {
              status: entry[`day${d}_status`] ?? null,
              shift_type_id: entry[`day${d}_shift_type_id`] ?? null,
              clock: entry[`day${d}_clock`] ?? null,
            };
          }
        }
        return {
          ...entry,
          hasExistingSchedule: Boolean(entry.hasExistingSchedule),
          employee: {
            user_id: entry.employee?.user_id ?? entry.user?.id ?? entry.user_id,
            emp_id: entry.employee?.emp_id ?? entry.user?.member_id ?? "",
            name: entry.employee?.name ?? entry.user?.name ?? "",
          },
          days,
          schedule_type: entry.schedule_type || this.scheduleType,
        };
      });
    },
    async fetchCompanySchedules() {
      this.loading = true;
      try {
        const response = await this.$axios.get(
          `/api/company-clock-settings/company/${this.companyId}`
        );
        const rawSettings = response.data?.data || response.data || [];
        this.schedules = rawSettings.filter(
          (s) => s && s.fixed_working_days === false
        );
        const uniqueTypeSet = new Set(
          this.schedules
            .map((s) => s?.schedule_type)
            .filter((v) => v !== null && v !== undefined)
        );
        uniqueTypeSet.add("GENERAL");
        const uniqueTypes = [
          "GENERAL",
          ...Array.from(uniqueTypeSet).filter((v) => v !== "GENERAL"),
        ];

        this.scheduleTypeOptions = uniqueTypes.map((val) => {
          const anySettingWithType = this.schedules.find(
            (s) => s?.schedule_type === val
          );
          const namePart = anySettingWithType?.name && val !== "GENERAL"
            ? `（${anySettingWithType.name}）`
            : "";
          return {
            text: `${this.formatScheduleType(val)}${namePart}`,
            value: val,
          };
        });

        if (!this.scheduleType && this.scheduleTypeOptions.length > 0) {
          this.scheduleType = this.scheduleTypeOptions[0].value;
        }
      } catch (error) {
        console.error("Error fetching company clock schedules:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("zh-TW");
    },
    formatScheduleType,
    async importSchedule() {
      this.importFile = null;
      this.canImport = false;
      this.importDialog = true;
      if (!this.schedules || this.schedules.length === 0) {
        await this.fetchCompanySchedules();
      }
    },
    // viewSchedule
    async viewScheduleByYM() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/api/scheduling/entries", {
          params: {
            company_id: this.companyId,
            year: this.viewYear,
            month: this.viewMonth,
          },
        });
        this.importedEntries = this.transformScheduleEntries(
          response.data.data || []
        );
        this.importedErrors = [];
        this.importedWarnings = [];
        this.importedNationalHolidaySummaries =
          response.data.nationalHolidaySummaries || [];
        this.importYear = this.viewYear;
        this.importMonth = this.viewMonth;
        const fallbackType = this.scheduleTypeOptions[0]?.value || null;
        this.scheduleType = this.scheduleType || fallbackType;
        this.showScheduleEntry = true;

        this.$nextTick(() => {
          const el =
            this.$refs.scheduleEntryWidget &&
            this.$refs.scheduleEntryWidget.$el;
          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      } catch (error) {
        console.error("Error fetching schedule by YM:", error);
        if (error.response && error.response.status === 404) {
          alert(`${this.viewYear}-${this.viewMonth} 找不到對應的班表`);
        } else {
          alert("載入排班明細失敗");
        }
      } finally {
        this.loading = false;
      }
    },
    cancelImport() {
      this.importDialog = false;
      this.importFile = null;
      this.scheduleType = null;
      this.canImport = false;
      this.importedEntries = [];
      this.importedErrors = [];
      this.importedWarnings = [];
      this.importedNationalHolidaySummaries = [];
    },
    async validateImport() {
      const data = await this.importScheduleRequest("validate");
      if (!data) return;

      if (data.errors && data.errors.length > 0) {
        alert(`驗證有 ${data.errors.length} 筆錯誤`);
        this.canImport = false;
        this.importDialog = false;
      } else {
        this.snackbar.show = true;
        this.snackbar.text = "驗證成功";
        this.snackbar.color = "success";
        this.canImport = true;
        this.importDialog = false;
      }
    },
    async performImport() {
      const data = await this.importScheduleRequest("import");
      if (!data) return;

      if (data.errors && data.errors.length > 0) {
        alert(`有 ${data.errors.length} 筆錯誤`);
      } else {
        this.snackbar.show = true;
        this.snackbar.text = "匯入成功";
        this.snackbar.color = "success";
      }

      this.importDialog = false;
      this.importFile = null;
      this.canImport = false;
    },
    async importScheduleRequest(mode) {
      const derivedScheduleType = this.scheduleType || null;
      if (!derivedScheduleType) {
        alert("請選擇排班類型");
        return null;
      }
      if (!this.importFile) {
        alert("請選擇檔案");
        return null;
      }

      const formData = new FormData();
      formData.append("file", this.importFile);
      formData.append("mode", mode);
      formData.append("company_id", this.companyId);
      formData.append("schedule_type", derivedScheduleType);
      formData.append("year", this.importYear);
      formData.append("month", this.importMonth);

      try {
        const response = await this.$axios.post(
          "/api/scheduling/imports/excel",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        const data = response.data || {};
        this.importedEntries = this.transformScheduleEntries(
          (data && data.data) || []
        );
        this.importedErrors = (data && data.errors) || [];
        this.importedWarnings = (data && data.warnings) || [];
        this.importedNationalHolidaySummaries =
          (data && data.nationalHolidaySummaries) || [];
        this.showScheduleEntry = true;
        this.scheduleType = derivedScheduleType;

        this.$nextTick(() => {
          const el =
            this.$refs.scheduleEntryWidget &&
            this.$refs.scheduleEntryWidget.$el;
          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });

        return data;
      } catch (error) {
        console.log("匯入失敗了耶");
        console.error("Error importing schedule:", error);
        // alert("匯入失敗");
        return null;
      }
    },
    openShiftDialog() {
      this.shiftDialog.visible = true;
    },
    editShiftType(shift) {
      this.shiftDialog.visible = true;
      // Pass shift to dialog if needed
      this.$nextTick(() => {
        this.$refs.shiftTypeDialog.editShift(shift);
      });
    },
    confirmDeleteShiftType(id) {
      if (confirm("確定要刪除此班別嗎？")) {
        this.deleteShiftType(id);
      }
    },
    async deleteShiftType(id) {
      try {
        await this.$axios.delete(`/api/scheduling/shift-types/${id}`, {
          params: { company_id: this.companyId },
        });
        this.fetchShiftTypes();
        this.snackbar.show = true;
        this.snackbar.text = "班別刪除成功";
        this.snackbar.color = "success";
      } catch (error) {
        console.error("Error deleting shift type:", error);
        this.snackbar.show = true;
        this.snackbar.text = "班別刪除失敗";
        this.snackbar.color = "error";
      }
    },
    async exportEmptySchedule() {
      try {
        const params = {
          company_id: this.companyId,
          year: this.viewYear,
          month: this.viewMonth,
        };
        const response = await this.$axios.get(
          "/api/scheduling/schedules/template",
          {
            params,
            responseType: "arraybuffer",
          }
        );

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const filename = `schedule-template-${this.viewYear}-${String(
          this.viewMonth
        ).padStart(2, "0")}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting empty schedule:", error);
        if (error.response && error.response.data) {
          alert("匯出失敗: " + (error.response.data.message || "未知錯誤"));
        } else {
          alert("匯出失敗");
        }
      }
    },
    async exportHourlySchedule() {
      try {
        const params = {
          company_id: this.companyId,
          year: this.viewYear,
          month: this.viewMonth,
        };
        const response = await this.$axios.get(
          "/api/scheduling/schedules/hourly-template",
          {
            params,
            responseType: "arraybuffer",
          }
        );

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const filename = `hourly-schedule-template-${this.viewYear}-${String(
          this.viewMonth
        ).padStart(2, "0")}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting hourly schedule:", error);
        if (error.response && error.response.data) {
          alert("匯出失敗: " + (error.response.data.message || "未知錯誤"));
        } else {
          alert("匯出失敗");
        }
      }
    },
    async openHistoryDialog(block) {
      if (!block.entry_id) return;
      this.historyDialog.entryId = block.entry_id;
      this.historyDialog.empName = `${block.emp_id} ${block.name}`;
      this.historyDialog.records = [];
      this.historyDialog.rollingBack = null;
      this.historyDialog.visible = true;
      try {
        const response = await this.$axios.get(
          `/api/scheduling/entries/${block.entry_id}/histories`
        );
        this.historyDialog.records = response.data?.data || [];
      } catch (error) {
        console.error("Error fetching history:", error);
        alert("取得歷史紀錄失敗");
      }
    },
    async confirmRollback(history) {
      if (
        !confirm(
          `確定要還原至 ${this.formatDateTime(
            history.create_time
          )} 的版本嗎？\n這將會將目前的排班資料替換為該歷史版本。`
        )
      ) {
        return;
      }
      this.historyDialog.rollingBack = history.id;
      try {
        await this.$axios.post(
          `/api/scheduling/entries/${this.historyDialog.entryId}/rollback/${history.id}`
        );
        this.snackbar.show = true;
        this.snackbar.text = "排班已還原成功";
        this.snackbar.color = "success";
        this.historyDialog.visible = false;
        // Refresh the schedule view
        await this.viewScheduleByYM();
      } catch (error) {
        console.error("Error rolling back:", error);
        const msg = error.response?.data?.message || "還原失敗";
        alert(msg);
      } finally {
        this.historyDialog.rollingBack = null;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(
        d.getDate()
      )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    summarizeDayStatuses(historyRecord) {
      const counts = {};
      for (let d = 1; d <= 31; d++) {
        const status = historyRecord[`day${d}_status`];
        if (status) {
          counts[status] = (counts[status] || 0) + 1;
        }
      }
      return Object.entries(counts)
        .map(([s, c]) => `${s}:${c}`)
        .join(" ");
    },
  },
};
</script>
